import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { Spinner, cx } from './ui.js';
import { haptic } from './device.js';

/**
 * Botão de deslizar para confirmar.
 *
 * Toque único é fácil demais de disparar sem querer com o celular no
 * suporte do carro balançando — "cheguei", "iniciar" e "aceitar" mudam o
 * estado de uma corrida de verdade. Arrastar até o fim exige intenção.
 *
 * Track ocupa quase a largura inteira da tela (o pai decide a largura via
 * className); o cabo é solto antes de 90% do percurso e volta sozinho —
 * só passar daí dispara a ação.
 */
const THRESHOLD = 0.9;

export function SlideToConfirm({
  label,
  onConfirm,
  loading,
  disabled,
  tone = 'brand',
  className,
}: {
  label: string;
  onConfirm: () => void;
  loading?: boolean;
  disabled?: boolean;
  tone?: 'brand' | 'danger';
  className?: string;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLButtonElement>(null);
  const dragState = useRef<{ pointerId: number; startX: number; max: number } | null>(null);
  const [drag, setDrag] = useState(0); // 0..1
  const [dragging, setDragging] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  // Loading/disabled de fora reseta o cabo — sem isto, uma resposta com
  // erro deixaria o cabo preso no fim, parecendo travado.
  useEffect(() => {
    if (!loading) setConfirmed(false);
  }, [loading]);

  function maxTravel(): number {
    const track = trackRef.current;
    const thumb = thumbRef.current;
    if (!track || !thumb) return 0;
    return track.clientWidth - thumb.clientWidth - 8; // 8 = padding do track
  }

  function onPointerDown(e: ReactPointerEvent) {
    if (disabled || loading || confirmed) return;
    const max = maxTravel();
    if (max <= 0) return;
    dragState.current = { pointerId: e.pointerId, startX: e.clientX, max };
    thumbRef.current?.setPointerCapture(e.pointerId);
    setDragging(true);
  }

  function onPointerMove(e: ReactPointerEvent) {
    const state = dragState.current;
    if (!state || state.pointerId !== e.pointerId) return;
    const delta = e.clientX - state.startX;
    const pct = Math.max(0, Math.min(1, delta / state.max));
    setDrag(pct);
  }

  function onPointerUp(e: ReactPointerEvent) {
    const state = dragState.current;
    if (!state || state.pointerId !== e.pointerId) return;
    dragState.current = null;
    setDragging(false);

    if (drag >= THRESHOLD) {
      setDrag(1);
      setConfirmed(true);
      haptic([10, 30, 10]);
      onConfirm();
    } else {
      setDrag(0);
    }
  }

  const pct = confirmed ? 1 : drag;
  const travelPx = pct * maxTravel();
  const toneClass =
    tone === 'danger'
      ? 'bg-[linear-gradient(155deg,#f87171,var(--c-danger)_60%,#a11616)]'
      : 'bg-[linear-gradient(155deg,var(--c-brand-soft),var(--c-brand)_55%,var(--c-brand-deep))]';

  return (
    <div
      ref={trackRef}
      className={cx(
        'relative h-16 w-full select-none overflow-hidden rounded-full border border-line bg-surface-2 p-1',
        (disabled || loading) && 'opacity-60',
        className,
      )}
    >
      {/* Trilha de cor por trás do cabo, cresce junto com o arrasto — é o
          que dá feedback contínuo de "quanto falta", não só o cabo solto. */}
      <div
        className={cx('absolute inset-y-1 left-1 rounded-full', toneClass, dragging ? 'duration-0' : 'transition-[width] duration-200')}
        style={{ width: `${travelPx + 56}px` }}
        aria-hidden="true"
      />

      <span
        className={cx(
          'pointer-events-none absolute inset-0 flex items-center justify-center gap-2 text-base font-semibold text-ink transition-opacity',
        )}
        style={{ opacity: Math.max(0, 1 - pct * 2.2) }}
      >
        {label}
        <svg viewBox="0 0 20 20" className="h-4 w-4 fill-faint" aria-hidden="true">
          <path d="M7 4l6 6-6 6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>

      <button
        ref={thumbRef}
        type="button"
        aria-label={label}
        disabled={disabled || loading || confirmed}
        // Enter/Espaço confirma direto, para quem usa teclado ou leitor
        // de tela — arrastar não é a única forma de operar o controle.
        onClick={() => {
          if (dragging || confirmed || disabled || loading) return;
          setDrag(1);
          setConfirmed(true);
          haptic([10, 30, 10]);
          onConfirm();
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className={cx(
          'relative z-10 grid h-14 w-14 shrink-0 place-items-center rounded-full text-white shadow-lg',
          'touch-none',
          toneClass,
          dragging ? 'duration-0' : 'transition-transform duration-200',
        )}
        style={{ transform: `translateX(${travelPx}px)` }}
      >
        {loading ? (
          <Spinner className="h-5 w-5" />
        ) : confirmed ? (
          <svg viewBox="0 0 20 20" className="h-6 w-6 fill-white" aria-hidden="true">
            <path d="M7.6 13.4 4.2 10l-1.4 1.4 4.8 4.8L17.2 6.6l-1.4-1.4z" />
          </svg>
        ) : (
          <svg viewBox="0 0 20 20" className="h-6 w-6 fill-white" aria-hidden="true">
            <path d="M7 4l6 6-6 6" stroke="white" strokeWidth="2.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>
    </div>
  );
}
